import React, { useState, useContext, useEffect } from 'react';
import { Box, Typography, Divider, Grid, Button, Drawer, useMediaQuery, CircularProgress } from '@mui/material';
import { Screen } from '../../components';
import { UserContext } from '../../lib/contexts/UserContext';
import { ThemeContext } from '../../lib/contexts/ThemeContext';
import { OrdersContext } from '../../lib/contexts/OrdersContext';
import { BenefitDetailsContext } from '../../lib/contexts/BenefitDetailsContext';
import useUserAddress from '../../lib/hooks/useUserAddress';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MiniInfoBlock from './components/subComponents/MiniInfoBlock';
import MiniAddressCard from './components/subComponents/MiniAddressCard';
import PreferenceSwitchBox from './components/subComponents/PreferenceSwitchBox';
import { determineUserFacingStatus } from './components/NewProgramInfoCard';
import NewProgramInfoCard from './components/NewProgramInfoCard';
import { formatAddress } from '../../lib/helpers/helpers';
import { toCapitalizeFromUnderScore } from '../../lib/helpers/helpers';
import { prettyDate } from '../../lib/helpers/helpers';
import useSession from '../../lib/hooks/useSession';

const AccountManagementScreenMobile = () => {
  const { state } = useContext(UserContext);
  const { theme } = useContext(ThemeContext);
  const { ordersData } = useContext(OrdersContext);
  const { mainBenefitDetails, benefitDetails } = useContext(BenefitDetailsContext);
  const { data: userAddressData } = useUserAddress();
  const { data: session } = useSession();
  const [workflowData, setWorkflowData] = useState({});
  const [menuOptions, setMenuOptions] = useState([
    'Personal Info',
    'Preferences',
    'Program History',
    'Application History',
  ]);
  const [selectedMenuOption, setSelectedMenuOption] = useState('');
  const [contactInfo, setContactInfo] = useState({
    phoneNumber: '',
    email: '',
    primaryAddress: '',
  });
  const [healthInfo, setHealthInfo] = useState({
    healthPlan: '',
    chronicConditions: '',
    DOB: '',
  });
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isAddAddress, setIsAddAddress] = useState(false);
  const [mainAddress, setMainAddress] = useState('');
  const [orderedAddresses, setOrderedAddresses] = useState([]);
  const isMobileView = useMediaQuery('(max-width: 600px)');

  // New states to track if default values should be set
  const [defaultContactInfo, setDefaultContactInfo] = useState(false);
  const [defaultHealthInfo, setDefaultHealthInfo] = useState(false);
  const [showDefaultAddressMessage, setShowDefaultAddressMessage] = useState(false);

  useEffect(() => {
    if (session) {
      setWorkflowData({
        currentDietQuality: session?.lastQuality,
        goalDietQuality: session?.goalQuality,
      });
    }
  }, [session]);

  useEffect(() => {
    const contactInfoTimeout = setTimeout(() => {
      setDefaultContactInfo(true);
    }, 10000); // 10 seconds

    const healthInfoTimeout = setTimeout(() => {
      setDefaultHealthInfo(true);
    }, 10000); // 10 seconds

    const addressTimeout = setTimeout(() => {
      if (orderedAddresses.length === 0 || (orderedAddresses.length === 1 && orderedAddresses[0] === null)) {
        setShowDefaultAddressMessage(true);
      }
    }, 10000); // 10 seconds

    return () => {
      clearTimeout(contactInfoTimeout);
      clearTimeout(healthInfoTimeout);
      clearTimeout(addressTimeout);
    };
  }, [orderedAddresses]);

  const programBenefits = [];
  const applicationBenefits = [];

  benefitDetails?.forEach((benefit) => {
    const userFacingStatus = determineUserFacingStatus(benefit, workflowData);
    const type = ['Active', 'Canceled', 'Completed'].includes(userFacingStatus) ? 'Program' : 'Application';

    if (type === 'Program') {
      programBenefits.push(benefit);
    } else {
      applicationBenefits.push(benefit);
    }
  });

  useEffect(() => {
    const tempArr = [];
    let tempDefaultAddress = null;
    if (userAddressData?.locations) {
      for (let info of userAddressData?.locations) {
        if (info?.is_default === 1) {
          tempDefaultAddress = info;
        } else {
          tempArr.push(info);
        }
      }
    }
    setOrderedAddresses([tempDefaultAddress, ...tempArr]);
  }, [userAddressData]);

  useEffect(() => {
    // console.log('state:', state);
    // console.log('userAddressData:', userAddressData);
    // console.log('mainBenefitDetails:', mainBenefitDetails);
    if (state && userAddressData?.locations) {
      let tempAddress = '';
      for (let info of userAddressData?.locations) {
        if (info?.is_default) {
          tempAddress = formatAddress(info);
        }
      }
      setContactInfo({
        phoneNumber: state?.user?.phone_number,
        email: state?.email,
        primaryAddress: tempAddress,
      });
    } else if (state) {
      setContactInfo({
        ...contactInfo,
        phoneNumber: state?.user?.phone_number,
        email: state?.email,
      });
    } else if (userAddressData?.locations) {
      setContactInfo({
        ...contactInfo,
        primaryAddress: tempAddress,
      });
    }
    if (state) {
      const tempGoalArr = state?.userObject?.lastWorkflow?.raw_responses?.ideal_goals?.for_health;
      let tempChronicConditionsString = '';
      for (let index in tempGoalArr) {
        if (index === tempGoalArr.length - 1) {
          tempChronicConditionsString += `${toCapitalizeFromUnderScore(tempGoalArr[index])}`;
        } else {
          tempChronicConditionsString += `${toCapitalizeFromUnderScore(tempGoalArr[index])}, `;
        }
      }
      const tempDOB = prettyDate(state?.DOB);
      setHealthInfo({
        chronicConditions: tempChronicConditionsString,
        DOB: tempDOB,
        healthPlan: mainBenefitDetails?.benefit_name,
      });
    }
  }, [state, mainBenefitDetails, userAddressData]);

  return (
    <Screen showSidebarNav={true}>
      {selectedMenuOption === '' && (
        <Box
          id="mobile-account-management-main-screen-container"
          sx={{
            marginTop: '20px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '16px',
            alignSelf: 'stretch',
          }}
        >
          <Box
            id="header-container"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: '4px',
              alignSelf: 'stretch',
            }}
          >
            <Typography
              sx={{
                color: 'var(--color12, #171717)',
                // fontFeatureSettings: 'liga' off, 'clig' off,
                fontFamily: 'Montserrat',
                fontSize: '24px',
                fontStyle: 'normal',
                fontWeight: 700,
                lineHeight: '133.4%',
              }}
            >
              Account
            </Typography>
          </Box>
          <Box
            id="main-body-container"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: '32px',
              alignSelf: 'stretch',
            }}
          >
            <Box
              id="mini-personal-info-container"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: '2px',
                alignSelf: 'stretch',
                borderRadius: '8px',
                maxWidth: '340px',
              }}
            >
              <Button
                sx={{
                  display: 'flex',
                  //   width: '158px',
                  width: '200px',
                  height: '44px',
                  padding: '0px 7px',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '18px',
                  borderRadius: '9px',
                }}
              >
                <Typography
                  sx={{
                    color: 'var(--color12, #171717)',
                    leadingTrim: 'both',
                    textEdge: 'cap',
                    fontFamily: 'Montserrat',
                    fontSize: '16px',
                    fontStyle: 'normal',
                    fontWeight: 700,
                    lineHeight: 'normal',
                  }}
                >
                  Personal Info
                </Typography>
                <ArrowForwardIcon
                  onClick={() => setSelectedMenuOption('Personal Info')}
                  sx={{
                    width: '24px',
                    height: '24px',
                    flexShrink: 0,
                  }}
                />
              </Button>
              <Box
                id="mini-personal-info-container"
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  gap: '8px',
                  alignSelf: 'stretch',
                }}
              >
                <MiniInfoBlock
                  title="Phone Number"
                  type="phone"
                  value={
                    defaultContactInfo && !contactInfo.phoneNumber ? 'No Phone Number found' : contactInfo.phoneNumber
                  }
                />
                <MiniInfoBlock
                  title="Email"
                  type="email"
                  value={defaultContactInfo && !contactInfo.email ? 'No Email found' : contactInfo.email}
                />
              </Box>
            </Box>
            <Box
              id="mini-address-container"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: '2px',
                alignSelf: 'stretch',
                borderRadius: '8px',
              }}
            >
              <Button
                sx={{
                  display: 'flex',
                  //   width: '158px',
                  width: '200px',
                  height: '44px',
                  padding: '0px 7px',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '18px',
                  borderRadius: '9px',
                }}
              >
                <Typography
                  sx={{
                    color: 'var(--color12, #171717)',
                    leadingTrim: 'both',
                    textEdge: 'cap',
                    fontFamily: 'Montserrat',
                    fontSize: '16px',
                    fontStyle: 'normal',
                    fontWeight: 700,
                    lineHeight: 'normal',
                  }}
                >
                  My Addresses
                </Typography>
                <ArrowForwardIcon
                  onClick={() => setSelectedMenuOption('My Addresses')}
                  sx={{
                    width: '24px',
                    height: '24px',
                    flexShrink: 0,
                  }}
                />
              </Button>
              {showDefaultAddressMessage ? (
                <Typography>No addresses found</Typography>
              ) : orderedAddresses.length === 0 || (orderedAddresses.length === 1 && orderedAddresses[0] === null) ? (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '100%',
                    height: '100%',
                    minHeight: '200px',
                  }}
                >
                  <CircularProgress />
                </Box>
              ) : (
                <MiniAddressCard
                  address={mainAddress}
                  firstName={state?.user?.first_name}
                  lastName={state?.user?.last_name}
                />
              )}
            </Box>
            {/* <Box
              id="mini-preferences-container"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: '2px',
                alignSelf: 'stretch',
                borderRadius: '8px',
              }}
            >
              <Button
                sx={{
                  display: 'flex',
                  //   width: '158px',
                  width: '200px',
                  height: '44px',
                  padding: '0px 7px',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '18px',
                  borderRadius: '9px',
                }}
              >
                <Typography
                  sx={{
                    color: 'var(--color12, #171717)',
                    leadingTrim: 'both',
                    textEdge: 'cap',
                    fontFamily: 'Montserrat',
                    fontSize: '16px',
                    fontStyle: 'normal',
                    fontWeight: 700,
                    lineHeight: 'normal',
                  }}
                >
                  Preferences
                </Typography>
                <ArrowForwardIcon
                  onClick={() => setSelectedMenuOption('Preferences')}
                  sx={{
                    width: '24px',
                    height: '24px',
                    flexShrink: 0,
                  }}
                />
              </Button>
              <Box
                id="mini-preferences-info-container"
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  gap: '8px',
                  alignSelf: 'stretch',
                }}
              >
                <PreferenceSwitchBox
                  checked={false}
                  onChange={() => {}}
                  switchItems={['SMS Notifications', 'Push Notifications', 'Email Notifications']}
                />
              </Box>
            </Box> */}
          </Box>
          {programBenefits.length > 0 && (
            <Box
              id="program-history-info-container"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: '2px',
                alignSelf: 'stretch',
                borderRadius: '8px',
                maxWidth: '340px',
              }}
            >
              <Button
                sx={{
                  display: 'flex',
                  //   width: '158px',
                  width: '200px',
                  height: '44px',
                  padding: '0px 7px',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '18px',
                  borderRadius: '9px',
                }}
              >
                <Typography
                  sx={{
                    color: 'var(--color12, #171717)',
                    leadingTrim: 'both',
                    textEdge: 'cap',
                    fontFamily: 'Montserrat',
                    fontSize: '16px',
                    fontStyle: 'normal',
                    fontWeight: 700,
                    lineHeight: 'normal',
                  }}
                >
                  My Programs
                </Typography>
                <ArrowForwardIcon
                  onClick={() => setSelectedMenuOption('My Programs')}
                  sx={{
                    width: '24px',
                    height: '24px',
                    flexShrink: 0,
                  }}
                />
              </Button>
              <Box
                id="mini-personal-info-container"
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  gap: '8px',
                  alignSelf: 'stretch',
                }}
              >
                <NewProgramInfoCard programInfo={programBenefits[0]} />
              </Box>
            </Box>
          )}
          {applicationBenefits.length > 0 && (
            <Box
              id="program-history-info-container"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: '2px',
                alignSelf: 'stretch',
                borderRadius: '8px',
                maxWidth: '340px',
              }}
            >
              <Button
                sx={{
                  display: 'flex',
                  //   width: '158px',
                  width: '220px',
                  height: '44px',
                  padding: '0px 7px',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '18px',
                  borderRadius: '9px',
                }}
              >
                <Typography
                  sx={{
                    color: 'var(--color12, #171717)',
                    leadingTrim: 'both',
                    textEdge: 'cap',
                    fontFamily: 'Montserrat',
                    fontSize: '16px',
                    fontStyle: 'normal',
                    fontWeight: 700,
                    lineHeight: 'normal',
                  }}
                >
                  My Applications
                </Typography>
                <ArrowForwardIcon
                  onClick={() => setSelectedMenuOption('My Applications')}
                  sx={{
                    width: '24px',
                    height: '24px',
                    flexShrink: 0,
                  }}
                />
              </Button>
              <Box
                id="mini-personal-info-container"
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  gap: '8px',
                  alignSelf: 'stretch',
                }}
              >
                <NewProgramInfoCard programInfo={applicationBenefits[0]} />
              </Box>
            </Box>
          )}
        </Box>
      )}
      {selectedMenuOption === 'Personal Info' && (
        <Box
          id="personal-info-container"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '24px',
            alignSelf: 'stretch',
          }}
        >
          <Box
            id="personal-info-header-container"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              alignSelf: 'stretch',
            }}
          >
            <ArrowBackIcon
              onClick={() => setSelectedMenuOption('')}
              sx={{
                width: '24px',
                height: '24px',
              }}
            />
            <Typography
              sx={{
                color: 'var(--Overlay-Black-black12, rgba(0, 0, 0, 0.91))',
                fontFeatureSettings: 'liga off, clig off',
                fontFamily: 'Montserrat',
                fontSize: '24px',
                fontStyle: 'normal',
                fontWeight: 700,
                lineHeight: '133.4%',
              }}
            >
              Personal Info
            </Typography>
          </Box>
          <Box
            id="personal-info-body-container"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '40px',
            }}
          >
            <Box
              id="contact-info-container"
              sx={{
                display: 'flex',
                width: '324px',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: '40px',
              }}
            >
              <Box
                id="contact-info-container-2"
                sx={{
                  display: 'flex',
                  width: '327px',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  gap: '16px',
                }}
              >
                <Typography
                  sx={{
                    alignSelf: 'stretch',
                    color: 'var(--Overlay-Black-black12, rgba(0, 0, 0, 0.91))',
                    fontFamily: 'Montserrat',
                    fontSize: '20px',
                    fontStyle: 'normal',
                    fontWeight: 700,
                    lineHeight: '125%',
                  }}
                >
                  Contact Info
                </Typography>
                <Box
                  id="contact-info-body"
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    gap: '16px',
                    alignSelf: 'stretch',
                  }}
                >
                  <MiniInfoBlock
                    title="Phone Number"
                    value={
                      defaultContactInfo && !contactInfo.phoneNumber ? 'No Phone Number found' : contactInfo.phoneNumber
                    }
                  />
                  <MiniInfoBlock
                    title="Email"
                    value={defaultContactInfo && !contactInfo.email ? 'No Email found' : contactInfo.email}
                  />
                  <MiniInfoBlock
                    title="Primary Address"
                    value={
                      defaultContactInfo && !contactInfo.primaryAddress
                        ? 'No Primary Address found'
                        : contactInfo.primaryAddress
                    }
                  />
                </Box>
              </Box>
            </Box>
            <Box
              id="health-info-container"
              sx={{
                display: 'flex',
                width: '327px',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: '16px',
                borderRadius: '8px',
              }}
            >
              <Typography
                sx={{
                  alignSelf: 'stretch',
                  color: 'var(--Overlay-Black-black12, rgba(0, 0, 0, 0.91))',
                  fontFamily: 'Montserrat',
                  fontSize: '20px',
                  fontStyle: 'normal',
                  fontWeight: 700,
                  lineHeight: '125%',
                }}
              >
                Health Info
              </Typography>
              <Box
                id="health-info-body"
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  gap: '16px',
                  alignSelf: 'stretch',
                }}
              >
                <MiniInfoBlock
                  title="Health Plan"
                  value={defaultHealthInfo && !healthInfo.healthPlan ? 'No Health Plan found' : healthInfo.healthPlan}
                />
                <MiniInfoBlock
                  title="Chronic Conditions"
                  value={
                    defaultHealthInfo && !healthInfo.chronicConditions
                      ? 'No Chronic Conditions found'
                      : healthInfo.chronicConditions
                  }
                />
                <MiniInfoBlock
                  title="Date of Birth"
                  value={defaultHealthInfo && !healthInfo.DOB ? 'No Date of Birth found' : healthInfo.DOB}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      )}
      {selectedMenuOption === 'Preferences' && (
        <Box
          id="preferences-container"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '24px',
            alignSelf: 'stretch',
          }}
        >
          <Box
            id="preferences-header-container"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              alignSelf: 'stretch',
            }}
          >
            <ArrowBackIcon
              onClick={() => setSelectedMenuOption('')}
              sx={{
                width: '24px',
                height: '24px',
              }}
            />
            <Typography
              sx={{
                color: 'var(--Overlay-Black-black12, rgba(0, 0, 0, 0.91))',
                fontFeatureSettings: 'liga off, clig off',
                fontFamily: 'Montserrat',
                fontSize: '24px',
                fontStyle: 'normal',
                fontWeight: 700,
                lineHeight: '133.4%',
              }}
            >
              Preferences
            </Typography>
          </Box>
          <Box
            id="preferences-body-container"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: '40px',
            }}
          >
            <Box
              id="notification-preferences-container"
              sx={{
                display: 'flex',
                width: '327px',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: '16px',
                borderRadius: '8px',
              }}
            >
              <Typography
                sx={{
                  alignSelf: 'stretch',
                  color: 'var(--Overlay-Black-black12, rgba(0, 0, 0, 0.91))',
                  fontFamily: 'Montserrat',
                  fontSize: '20px',
                  fontStyle: 'normal',
                  fontWeight: 700,
                  lineHeight: '125%',
                }}
              >
                Notification Preferences
              </Typography>
              <PreferenceSwitchBox
                checked={false}
                onChange={() => {}}
                switchItems={['SMS Notifications', 'Push Notifications', 'Email Notifications']}
              />
            </Box>
            <Box
              id="reminder-preferences-container"
              sx={{
                display: 'flex',
                width: '327px',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: '16px',
                borderRadius: '8px',
              }}
            >
              <Typography
                sx={{
                  alignSelf: 'stretch',
                  color: 'var(--Overlay-Black-black12, rgba(0, 0, 0, 0.91))',
                  fontFamily: 'Montserrat',
                  fontSize: '20px',
                  fontStyle: 'normal',
                  fontWeight: 700,
                  lineHeight: '125%',
                }}
              >
                Reminder Preferences
              </Typography>
              <PreferenceSwitchBox
                checked={false}
                onChange={() => {}}
                switchItems={['Program Messages', 'Marketing Messages', 'Challenge Reminders']}
              />
            </Box>
          </Box>
        </Box>
      )}
      {selectedMenuOption === 'My Addresses' && (
        <Box
          id="my-addresses-container"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '24px',
            alignSelf: 'stretch',
          }}
        >
          <Box
            id="personal-info-header-container"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              alignSelf: 'stretch',
            }}
          >
            <ArrowBackIcon
              onClick={() => setSelectedMenuOption('')}
              sx={{
                width: '24px',
                height: '24px',
              }}
            />
            <Typography
              sx={{
                color: 'var(--Overlay-Black-black12, rgba(0, 0, 0, 0.91))',
                fontFeatureSettings: 'liga off, clig off',
                fontFamily: 'Montserrat',
                fontSize: '24px',
                fontStyle: 'normal',
                fontWeight: 700,
                lineHeight: '133.4%',
              }}
            >
              My Addresses
            </Typography>
          </Box>
          <Box
            id="delivery-addresses-info-box-container"
            sx={{
              display: 'flex',
              width: '324px',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '16px',
              borderRadius: '8px',
              maxHeight: '600px',
              overflowY: 'auto',
            }}
          >
            <Typography
              sx={{
                alignSelf: 'stretch',
                color: 'var(--color12, #171717)',
                fontFamily: 'Montserrat',
                fontSize: '20px',
                fontStyle: 'normal',
                fontWeight: 700,
                lineHeight: '125%', // 25px
              }}
            >
              Delivery Addresses
            </Typography>
            <Box
              id="delivery-addresses-container"
              sx={{
                maxHeight: '700px',
                overflowY: 'auto',
              }}
            >
              {orderedAddresses?.map((address, index) => (
                <MiniAddressCard
                  key={index}
                  address={address}
                  firstName={state?.user?.first_name}
                  lastName={state?.user?.last_name}
                />
              ))}
            </Box>
            <Button
              id="add-address-button"
              sx={{
                display: 'flex',
                width: '311px',
                height: 'var(--4, 44px)',
                padding: 'var(--0, 0px) var(--4, 18px)',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 'var(--4, 18px)',
                borderRadius: 'var(--4, 9px)',
                border: 'var(--1, 2px) solid var(--color12, #1C124D)',
              }}
              onClick={() => handleDrawerOpen(true)}
            >
              <Typography
                sx={{
                  color: 'var(--color12, #1C124D)',
                  textAlign: 'center',
                  fontFamily: 'Montserrat',
                  fontSize: '14px',
                  fontWeight: 700,
                }}
              >
                Add New Address
              </Typography>
            </Button>
          </Box>
        </Box>
      )}
      {selectedMenuOption === 'My Programs' && (
        <Box
          id="my-programs-container"
          sx={{
            display: 'flex',
            width: '327px',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '24px',
          }}
        >
          <Box
            id="my-programs-header-container"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '20px',
              alignSelf: 'stretch',
            }}
          >
            <Box
              id="my-programs-header-text-container"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                alignSelf: 'stretch',
              }}
            >
              <ArrowBackIcon
                onClick={() => setSelectedMenuOption('')}
                sx={{
                  width: '24px',
                  height: '24px',
                }}
              />
              <Typography
                sx={{
                  color: 'var(--color12, #1C124D)',
                  fontFeatureSettings: 'liga off, clig off',
                  fontFamily: 'Montserrat',
                  fontSize: '24px',
                  fontStyle: 'normal',
                  fontWeight: 700,
                  lineHeight: '133.4%',
                }}
              >
                Program History
              </Typography>
            </Box>
          </Box>
          <Box
            id="my-programs-body-container"
            sx={{
              display: 'flex',
              width: '327px',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'flex-start',
              borderRadius: '8px',
              border: '1px solid rgba(0, 0, 0, 0.12)',
              background: '#FFF',
            }}
          >
            <Box>
              {programBenefits?.map((benefit, index) => (
                <NewProgramInfoCard
                  key={index}
                  programInfo={benefit}
                />
              ))}
            </Box>
          </Box>
        </Box>
      )}
      {selectedMenuOption === 'My Applications' && (
        <Box
          id="my-applications-container"
          sx={{
            display: 'flex',
            width: '327px',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '24px',
          }}
        >
          <Box
            id="my-applications-header-container"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '20px',
              alignSelf: 'stretch',
            }}
          >
            <Box
              id="my-applications-header-text-container"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                alignSelf: 'stretch',
              }}
            >
              <ArrowBackIcon
                onClick={() => setSelectedMenuOption('')}
                sx={{
                  width: '24px',
                  height: '24px',
                }}
              />
              <Typography
                sx={{
                  color: 'var(--color12, #1C124D)',
                  fontFeatureSettings: 'liga off, clig off',
                  fontFamily: 'Montserrat',
                  fontSize: '24px',
                  fontStyle: 'normal',
                  fontWeight: 700,
                  lineHeight: '133.4%',
                }}
              >
                Application History
              </Typography>
            </Box>
          </Box>
          <Box
            id="my-applications-body-container"
            sx={{
              display: 'flex',
              width: '327px',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'flex-start',
              borderRadius: '8px',
              border: '1px solid rgba(0, 0, 0, 0.12)',
              background: '#FFF',
            }}
          >
            <Box>
              {applicationBenefits?.map((benefit, index) => (
                <NewProgramInfoCard
                  key={index}
                  programInfo={benefit}
                />
              ))}
            </Box>
          </Box>
        </Box>
      )}
    </Screen>
  );
};

export default AccountManagementScreenMobile;
